import type { FulfillmentOrderLine } from "../types";
import type { EntryParseBaseInput, EntryParseResult } from "./confirmationEntryTypes";
import { normalized, factors, unit, EntryParseError, parseEntryDates, parseEntrySegments } from "./confirmationEntryPrimitives";
import { calculateFulfillmentLineAmount, validateFulfillmentLineDraft } from "./fulfillmentOrderEditorService";

/** 本分类显式决定日期来源，日期范围不隐式参与计次数量。 */
function datePatch(input: EntryParseBaseInput, range = input.range): Partial<FulfillmentOrderLine> {
  return parseEntryDates(input.routeDays, range, "服务", "text");
}

/** 本分类服务主体资格与结算主体分别保留。 */
function providerPatch(input: EntryParseBaseInput): Partial<FulfillmentOrderLine> {
  const supplier = input.provider && "id" in input.provider ? input.provider : null;
  if (supplier && "snapshotResourceType" in supplier) {
    if (supplier.snapshotResourceType !== "包车") throw new EntryParseError(`服务主体“${supplier.name}”属于${supplier.snapshotResourceType}，不能用于包车；请重新选择本类服务主体`, "provider");
  } else if (supplier) {
    if (!supplier.canBeFulfillmentSubject) throw new EntryParseError(`服务主体“${supplier.name}”不具备当前履约资格，请重新选择可履约的主体`, "provider");
  }
  return {
    fulfillmentSupplierId: supplier?.id ?? null,
    fulfillmentSupplierName: supplier?.name ?? null,
    settlementSupplierId: input.settlement?.id ?? null,
    settlementSupplierName: input.settlement?.name ?? null,
  };
}
/** 本分类明确资源类型和计价单位后校验完整行。 */
function basePatch(input: EntryParseBaseInput, name: string, quantity: string, count: string, price: string, quantityUnit: string, extra: Partial<FulfillmentOrderLine> = {}): Partial<FulfillmentOrderLine> {
  const amount = calculateFulfillmentLineAmount(quantity, count, price);
  const patch: Partial<FulfillmentOrderLine> = {
    resourceType: "包车", itemNameSnapshot: name, itemSpecSnapshot: null,
    quantity, billingCount: count, unitPriceAmount: price, amount, unit: quantityUnit,
    pricingUnitType: "unit",
    reviewStatus: "confirmed", sourceType: "manual", status: "active", ...datePatch(input), ...providerPatch(input), ...extra,
  };
  const errors = validateFulfillmentLineDraft({ ...patch, id: input.allowZeroQuantity ? "existing-preview" : "local-preview" } as FulfillmentOrderLine);
  if (errors.length) throw new Error(errors.join("；"));
  return patch;
}
/** 解析本类单段原文，保留本类资源和计费约束。 */
function parseSegment(input: EntryParseBaseInput, text: string): Partial<FulfillmentOrderLine>[] {
  const raw = normalized(text);
  const match = raw.replace(/，/g, ",").match(/^([dD\d,-]+)\s+(?:(当地用车|包车|接送|单接|单送)\s+)?(.+)$/);
  if (!match) throw new EntryParseError("用车缺少行首日期，请在文本开头填写实际出行日，例如1 单接 @47座 1辆×100；日期以文本为准", "range", "text");
  const days = datePatch(input, match[1]).dayNumbers!;
  const purpose = match[2] || (match[1].includes("-") ? "当地用车" : "");
  if (!purpose) throw new Error("单日或分散日期须明确单接、单送或当地用车");
  if (purpose === "接送" && (days.length !== 2 || match[1].includes("-"))) throw new Error("接送须明确两个日期，例如1,5 接送");
  if (["单接", "单送"].includes(purpose) && days.length !== 1) throw new Error("单接或单送每笔只能一天");
  const segments = purpose === "接送" ? days.map((day, index) => ({ range: String(day), semantic: index ? "pickup" as const : "dropoff" as const }))
    : [{ range: match[1], semantic: purpose === "单接" ? "pickup" as const : purpose === "单送" ? "dropoff" as const : "local_charter" as const }];
  return match[3].split(",").flatMap((part) => {
    const vehicle = part.trim().match(/^@?(\d+座)(?:\s+(.+))?$/);
    if (!vehicle || !input.candidates.some((item) => item.resourceType === "包车" && [item.vehicleSpecName, item.skuName, item.mentionText].includes(vehicle[1]))) throw new EntryParseError(`车型“${vehicle?.[1] || part.trim()}”尚未关联有效候选，请输入@选择真实候选中的车型，再填写辆数×单价`, "resource");
    if (!vehicle[2]) throw new Error("尚未填写用车公式，请在车型后填写辆数×单价，例如1辆×100元；多次用车可填写辆数×次数×单价");
    const p = factors(vehicle[2], undefined, input.allowZeroQuantity);
    if (p.length < 2) throw new Error("请填写车辆数×单价或车辆数×次数×单价");
    unit(p[0], ["辆"]); if (p.length === 3) unit(p[1], ["次", "整段"]);
    return segments.map((segment) => basePatch({ ...input, range: segment.range }, segment.semantic === "pickup" ? "单接" : segment.semantic === "dropoff" ? "单送" : "当地用车", p[0].value, p.length === 3 ? p[1].value : "1", p.at(-1)!.value, "辆",
      { ...datePatch(input, segment.range), itemSpecSnapshot: vehicle[1], resourceSelection: { kind: "bus", vehicleSpecName: vehicle[1], entrySemantic: segment.semantic } }));
  });
}

/** 独立解析包车文本，保留本类日期、计费和资源规则。 */
export function parseBusEntry(input: EntryParseBaseInput): EntryParseResult {
  return parseEntrySegments(input.text, text => parseSegment(input, text), patch => [...(!patch.settlementSupplierId ? ["结算主体待设置"] : []), ...(!patch.fulfillmentSupplierId ? ["服务主体待核对"] : [])]);
}
